/**
 * Feedback Service
 *
 * Handles submission of user feedback via the feedback API module:
 *   - Chat feedback (thumbs up/down on AI messages) → POST /api/feedback/chat
 *   - Generic platform feedback (from header dialog) → POST /api/feedback
 *
 * Both endpoints are authenticated via Auth0 JWT and store feedback in DynamoDB.
 */

// Cached API URL loaded from aws-exports.json
let FEEDBACK_API_URL = ""

/**
 * Load the chat feedback API URL from aws-exports.json configuration.
 *
 * Points to /api/feedback/chat on the shared API Gateway.
 *
 * @returns The full URL for the chat feedback API endpoint.
 */
async function loadApiUrl(): Promise<string> {
  if (FEEDBACK_API_URL) {
    return FEEDBACK_API_URL
  }

  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    // VITE_API_URL env var takes precedence (used with npm run dev --host).
    // Falls back to apiUrl from aws-exports.json (CI-generated for deployed envs).
    // Last resort: relative path (works behind CloudFront).
    const baseUrl = import.meta.env.VITE_API_URL || config.apiUrl || ""
    FEEDBACK_API_URL = baseUrl
      ? `${baseUrl}/api/feedback/chat`
      : "/api/feedback/chat"
    return FEEDBACK_API_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("Feedback API URL not configured")
  }
}

export interface FeedbackPayload {
  sessionId: string
  message: string
  feedbackType: "positive" | "negative"
  userName: string
  agent: string
  /** The user message that preceded the assistant response being rated. */
  userMessage?: string
  comment?: string
  /** Segments from the assistant message (text + tool calls) for richer context. */
  segments?: Array<{
    type: "text" | "tool"
    content?: string
    toolCall?: {
      name: string
      input: string
      result?: string
    }
  }>
  feedbackScope?: "message" | "highlight" | "tool"
}

export interface FeedbackResponse {
  success: boolean
  feedbackId: string
}

/**
 * Submit chat feedback to the backend API.
 *
 * Sends a POST request to /api/feedback/chat with the user's rating and optional
 * comment. The request is authenticated via the Auth0 JWT in the Authorization
 * header.
 *
 * @param payload - Feedback data including sessionId, message, feedbackType, and optional comment.
 * @param idToken - Auth0 access token for API authentication.
 * @returns Promise resolving to the feedback response with the generated feedbackId.
 */
export async function submitChatFeedback(
  payload: FeedbackPayload,
  idToken: string
): Promise<FeedbackResponse> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  return await response.json() as FeedbackResponse
}

// ─── Generic Platform Feedback ───────────────────────────────────────────────

// Cached URL for the generic feedback endpoint
let GENERIC_FEEDBACK_API_URL = ""

/**
 * Load the generic platform feedback API URL from aws-exports.json configuration.
 *
 * Uses the /api/feedback endpoint (distinct from the chat-specific
 * /api/feedback/chat endpoint).
 *
 * @returns The full URL for the generic feedback API endpoint.
 */
async function loadGenericFeedbackApiUrl(): Promise<string> {
  if (GENERIC_FEEDBACK_API_URL) {
    return GENERIC_FEEDBACK_API_URL
  }

  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    const baseUrl = import.meta.env.VITE_API_URL || config.apiUrl || ""
    GENERIC_FEEDBACK_API_URL = baseUrl
      ? `${baseUrl}/api/feedback`
      : "/api/feedback"
    return GENERIC_FEEDBACK_API_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("Generic feedback API URL not configured")
  }
}

/**
 * Payload for generic platform feedback submitted from the header dialog.
 */
export interface GenericFeedbackPayload {
  /** The feedback message typed by the user. */
  message: string
  /** The display name or email of the user submitting feedback. */
  userName: string
  /** The route path the user was on when they submitted feedback (e.g. "/datasets"). */
  pagePath: string
  /** Optional base64-encoded PNG screenshot of the page at the time of submission. */
  screenshot?: string
}

/**
 * Submit generic platform feedback to the backend API.
 *
 * Sends a POST request to /api/feedback with the user's message, identity,
 * and the page path they were viewing when they submitted. Authenticated via
 * the Auth0 JWT in the Authorization header.
 *
 * @param payload - The feedback message, user name, and page path.
 * @param idToken - Auth0 access token for API authentication.
 * @returns Promise resolving to the feedback response with the generated feedbackId.
 */
export async function submitGenericFeedback(
  payload: GenericFeedbackPayload,
  idToken: string
): Promise<FeedbackResponse> {
  const apiUrl = await loadGenericFeedbackApiUrl()

  const response = await fetch(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  return await response.json() as FeedbackResponse
}
